import { useId } from 'react'

import Icon from '@components/utils/Icon'
import cn from '@components/utils/cn'

import timeFormatter from '@frontend/utils/timeFormatter'
import { scopedTranslation } from '@utils/I18n'
import { ShiftPublishState } from '@frontend/graphql/types.generated'

import { RosterShiftCard } from './RosterShiftCard'
import { RosterShiftRow } from './RosterShiftRow'
import { SelectionCheckbox } from './SelectionCheckbox'
import { useShiftsStore } from './stores/useShiftsStore'
import type { LayoutMode } from './layoutMode'

import { AddShiftButton } from '../author_shifts/AddShiftButton'
import { useEventTimeZone } from '../author_shifts/ShiftAuthoringContext'

import type { ShiftWithSlots } from '../shifts/types'

const t = scopedTranslation('roster_board')

const pendingRemovalLast = (a: ShiftWithSlots, b: ShiftWithSlots): number =>
  Number(a.publishState === ShiftPublishState.PendingRemoval) -
  Number(b.publishState === ShiftPublishState.PendingRemoval)

type RosterDayGroupProps = {
  date: string
  shifts: ShiftWithSlots[]
  layout: LayoutMode
  onChange: () => void
}

export function RosterDayGroup({ date, shifts, layout, onChange }: RosterDayGroupProps) {
  const dayLabel = timeFormatter.fromISO(date, useEventTimeZone()).toLongDate()
  const orderedShifts = [...shifts].sort(pendingRemovalLast)

  const daySelectId = useId()
  const selectedShiftIds = useShiftsStore((state) => state.selectedShiftIds)
  const setDaySelection = useShiftsStore((state) => state.setDaySelection)
  const dayIds = shifts.map((shift) => shift.id)
  const allSelected = dayIds.length > 0 && dayIds.every((id) => selectedShiftIds.has(id))
  const someSelected = dayIds.some((id) => selectedShiftIds.has(id))

  const dayHeader = (
    <div className="tw:flex tw:items-center tw:gap-2.5 tw:text-sm tw:text-gray-700">
      {dayIds.length > 0 && (
        <SelectionCheckbox
          id={daySelectId}
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={() => setDaySelection(dayIds, !allSelected)}
          aria-label={t('bulk.select_all_day', { day: dayLabel })}
        />
      )}
      <label
        htmlFor={dayIds.length > 0 ? daySelectId : undefined}
        className={cn('tw:m-0 tw:flex tw:items-center tw:gap-2.5', dayIds.length > 0 && 'tw:cursor-pointer')}
      >
        <Icon type="calendar" className="tw:h-4 tw:w-4 tw:text-gray-500" aria-hidden />
        <span className="tw:font-bold tw:text-gray-900">{dayLabel}</span>
      </label>
      <span className="tw:ml-auto tw:text-gray-500 tw:tabular-nums">{t('shift_count', { count: shifts.length })}</span>
    </div>
  )

  return (
    <section aria-label={dayLabel} className="tw:flex tw:flex-col tw:gap-3">
      {shifts.length === 0 ? (
        <>
          {dayHeader}
          <p
            data-testid="roster-day-empty"
            className="tw:rounded-xl tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:px-4 tw:py-6 tw:text-center tw:text-sm tw:text-gray-500"
          >
            {t('day_empty')}
          </p>
        </>
      ) : layout === 'cards' ? (
        <>
          {dayHeader}
          <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2 tw:xl:grid-cols-3">
            {orderedShifts.map((shift) => (
              <RosterShiftCard key={shift.id} shift={shift} onChange={onChange} />
            ))}
          </div>
        </>
      ) : (
        <div className="tw:overflow-hidden tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:shadow-xs">
          <div className="tw:border-b tw:border-gray-200 tw:bg-gray-50 tw:px-3.5 tw:py-2.5">{dayHeader}</div>
          <div className="tw:divide-y tw:divide-gray-100">
            {orderedShifts.map((shift) => (
              <RosterShiftRow key={shift.id} shift={shift} onChange={onChange} />
            ))}
          </div>
        </div>
      )}

      <div>
        <AddShiftButton presetDate={date} />
      </div>
    </section>
  )
}
